import { useAuth } from "@dl/web-core-js/auth";
import { Button } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Printer } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NexpimView } from "../../views";
import { formatNumber, SupplierClient } from "../../api/manufacturing";
import { WarehouseClient } from "../../api/product";
import { lineTotal, PurchaseOrderClient, PurchaseOrderLineClient } from "../../api/purchasing";
import { formatMoney, formatPrice } from "./replenishment/shared";

/**
 * The order as the supplier receives it: a sheet the browser prints.
 *
 * <p>Printing hides everything on the page but the sheet, so the app's own
 * navigation never reaches paper. Prices, quantities and the supplier's own
 * part numbers are what a supplier acts on; the internal part number is kept
 * beside them so a phone call about "line 3" has one answer on both ends.</p>
 */
export function PurchaseOrderPrintPage({ orderId }: { orderId: number }) {
  const { hasView } = useAuth();
  return hasView(NexpimView.PURCHASING) ? <PurchaseOrderPrint orderId={orderId} /> : <AccessDenied />;
}

/** Shown in place of a hidden page reached by its URL (the SIGM MFI replacement is dark; docs/sigm-mfi-replacement/dark-launch.md). */
function AccessDenied() {
  const { t } = useTranslation();
  return (
    <div className="p-4 lg:p-6">
      <p className="text-[13px] text-ink-muted">
        {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
      </p>
    </div>
  );
}

function PurchaseOrderPrint({ orderId }: { orderId: number }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const navigate = useNavigate();
  const orders = useMemo(() => new PurchaseOrderClient(http), [http]);
  const lines = useMemo(() => new PurchaseOrderLineClient(http), [http]);

  const order = useQuery({ queryKey: ["purchasing", "purchase-order", orderId], queryFn: () => orders.findById(orderId) });
  const rows = useQuery({
    queryKey: ["purchasing", "purchase-order-line", orderId],
    queryFn: async () =>
      (await lines.find({ page: 0, size: 1000, filters: { purchase_order_id: [String(orderId)] }, sort: ["position,asc"] })).content,
  });
  const supplier = useQuery({
    queryKey: ["supplier", "one", order.data?.supplier_id],
    queryFn: () => new SupplierClient(http).findById(order.data!.supplier_id),
    enabled: Boolean(order.data),
  });
  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    staleTime: 30 * 60_000,
  });

  const data = order.data;
  if (!data) {
    return (
      <div className="p-6 text-[13px] text-ink-muted">
        {order.isLoading ? t("common.loading", "Loading…") : t("po.not-found", "That order could not be loaded.")}
      </div>
    );
  }

  const warehouse = (warehouses.data ?? []).find((w) => w.id === data.warehouse_id);
  const items = rows.data ?? [];
  const subtotal = items.reduce((sum, line) => sum + lineTotal(line), 0);
  // Grouped, two decimals; a unit price keeps up to four, or quantity × price
  // would not come to the amount printed beside it (labels at 0.023 each).
  const money = (value: number) => formatMoney(value);
  const s = supplier.data;

  return (
    <div className="p-4 lg:p-6">
      {/* Only the sheet reaches paper. The margin is the page's, not the
          sheet's padding, so every page gets it and not only the first; a line
          is never cut across two pages. */}
      <style>{`@media print {
        body * { visibility: hidden; }
        .po-sheet, .po-sheet * { visibility: visible; }
        body { background: white; }
        .po-sheet { position: absolute; left: 0; top: 0; width: calc(8.5in - 24mm); max-width: none; margin: 0; padding: 0; box-shadow: none; border: 0; }
        .po-sheet tr { break-inside: avoid; }
        @page { size: letter; margin: 12mm; }
      }`}</style>

      <div className="mb-4 flex gap-2 print:hidden">
        <Button variant="ghost" onClick={() => void navigate({ to: "/purchasing/purchase-orders/$orderId", params: { orderId: String(orderId) } })}>
          {t("po.print.back", "Back to the order")}
        </Button>
        <Button variant="primary" onClick={() => window.print()}>
          <Printer className="size-4" />
          {t("po.print", "Print")}
        </Button>
      </div>

      <article className="po-sheet mx-auto max-w-[8.5in] rounded-md border border-line bg-white p-10 text-[12px] text-black shadow-sm">
        <header className="flex items-start justify-between border-b border-black pb-4">
          <div>
            <h1 className="text-[20px] font-semibold tracking-tight">{t("po.print.title", "Purchase order")}</h1>
            <p className="mt-1 text-[13px]">
              {t("po.print.number", "No. {{n}}", { n: data.number })}
              {(data.status === "DRAFT" || data.status === "AWAITING_APPROVAL") && (
                <span className="ml-3 rounded border border-black px-1.5 py-0.5 text-[10px] uppercase tracking-wide">
                  {data.status === "DRAFT" ? t("po.status.draft", "Draft") : t("po.print.not-approved", "Not approved")}
                </span>
              )}
              {data.status === "ISSUED" && data.is_on_hold && (
                <span className="ml-3 rounded border border-black px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                  {t("po.print.on-hold", "On hold: do not ship until released")}
                </span>
              )}
            </p>
          </div>
          <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5 text-right">
            <dt className="text-black/60">{t("column.po.order-date", "Order date")}</dt>
            <dd>{data.order_date}</dd>
            {data.required_date && (
              <>
                <dt className="text-black/60">{t("column.po.required-date", "Required")}</dt>
                <dd>{data.required_date}</dd>
              </>
            )}
            {data.supplier_reference && (
              <>
                <dt className="text-black/60">{t("po.print.your-reference", "Your reference")}</dt>
                <dd>{data.supplier_reference}</dd>
              </>
            )}
            <dt className="text-black/60">{t("column.currency", "Currency")}</dt>
            <dd>{data.currency}</dd>
          </dl>
        </header>

        <section className="grid grid-cols-2 gap-8 py-5">
          <div>
            <h2 className="mb-1 text-[10px] uppercase tracking-wider text-black/60">{t("po.print.to", "Supplier")}</h2>
            <p className="font-medium">{s?.legal_name || s?.name || data.supplier_name}</p>
            {s?.address_line && <p>{s.address_line}</p>}
            {(s?.city || s?.postal_code) && <p>{[s?.city, s?.postal_code].filter(Boolean).join(" ")}</p>}
            {s?.phone && <p>{s.phone}</p>}
          </div>
          <div>
            <h2 className="mb-1 text-[10px] uppercase tracking-wider text-black/60">{t("po.print.ship-to", "Deliver to")}</h2>
            <p className="font-medium">{warehouse?.name ?? data.warehouse_code}</p>
          </div>
        </section>

        <table className="w-full border-collapse tabular-nums">
          <thead>
            <tr className="border-y border-black text-left text-[10px] uppercase tracking-wider">
              <th className="py-1.5 pr-2">{t("po.print.item", "Item")}</th>
              <th className="py-1.5 pr-2">{t("po.print.your-number", "Your no.")}</th>
              <th className="py-1.5 pr-2 text-right">{t("column.bom.quantity", "Qty")}</th>
              <th className="py-1.5 pr-2 text-right">{t("column.po.price", "Price")}</th>
              <th className="py-1.5 text-right">{t("po.print.amount", "Amount")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((line) =>
              line.kind === "TEXT" ? (
                <tr key={line.id} className="border-b border-black/10">
                  <td colSpan={5} className="py-1.5 italic">{line.description}</td>
                </tr>
              ) : (
                <tr key={line.id} className="border-b border-black/10 align-top">
                  <td className="py-1.5 pr-2">
                    {line.kind === "EXPENSE" ? (
                      <div className="font-medium">{line.description}</div>
                    ) : (
                      <>
                        <div className="font-medium">{line.part_number}</div>
                        <div className="text-black/70">{line.description || line.part_name}</div>
                      </>
                    )}
                    {line.note && <div className="text-black/60">{line.note}</div>}
                  </td>
                  <td className="py-1.5 pr-2">{line.supplier_part_number ?? ""}</td>
                  <td className="py-1.5 pr-2 text-right">{formatNumber(line.quantity)}</td>
                  <td className="py-1.5 pr-2 text-right">{formatPrice(Number(line.unit_price))}</td>
                  <td className="py-1.5 text-right">{money(lineTotal(line))}</td>
                </tr>
              ),
            )}
          </tbody>
        </table>

        <div className="mt-4 flex justify-end">
          <dl className="grid min-w-56 grid-cols-[1fr_auto] gap-x-6 gap-y-0.5 tabular-nums">
            <dt>{t("column.po.subtotal", "Subtotal")}</dt>
            <dd className="text-right">{money(subtotal)}</dd>
            {Number(data.shipping_cost) > 0 && (
              <>
                <dt>{t("po.shipping", "Shipping")}</dt>
                <dd className="text-right">{money(Number(data.shipping_cost))}</dd>
              </>
            )}
            <dt className="border-t border-black pt-1 font-semibold">{t("po.print.total", "Total before taxes")}</dt>
            <dd className="border-t border-black pt-1 text-right font-semibold">
              {money(subtotal + Number(data.shipping_cost ?? 0))} {data.currency}
            </dd>
          </dl>
        </div>

        {data.note && <p className="mt-6 whitespace-pre-wrap border-t border-black/20 pt-3">{data.note}</p>}
      </article>
    </div>
  );
}
